import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Copy,
  Eye,
  Loader2,
  Save,
  Trash2,
  Undo2,
  Upload,
} from "lucide-react";
import type { ThemeTemplate } from "../types";
import { themeApi } from "../api";
import {
  THEME_DENSITY_META,
  THEME_EFFECT_META,
  THEME_SHAPE_META,
} from "../meta";
import {
  buildLocalStateFromTemplate,
  serializeToTokens,
  statesEqual,
  type ThemeMode,
  type ThemeStateLocal,
} from "../themeState";
import { ColorTokensEditor } from "./ColorTokensEditor";
import { SliderEditor } from "./SliderEditor";
import { TypographyEditor } from "./TypographyEditor";
import { LivePreview } from "./LivePreview";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { getToken } from "@/lib/api/client";
import { cn } from "@/lib/utils/cn";
import { usePermission } from "@/lib/permissions/useCan";
import { PERMISSIONS, PERMISSION_ALL } from "@/lib/permissions/catalog";
import { useSession } from "@/lib/auth/session";

interface Props {
  template: ThemeTemplate;
  onDeleted: () => void;
}

export function TemplateEditor({ template, onDeleted }: Props) {
  const qc = useQueryClient();
  const { push } = useToast();

  // Permisos granulares. Owner pasa por wildcard `*`. Admin/Designer
  // tienen THEME_EDIT_DRAFT pero NO THEME_PUBLISH ni THEME_ACTIVATE,
  // así que ven la pantalla y editan borradores pero NO ven los
  // botones de Publicar/Despublicar/Aplicar a producción.
  const canEditDraft = usePermission(PERMISSIONS.THEME_EDIT_DRAFT);
  const canPublish = usePermission(PERMISSIONS.THEME_PUBLISH);
  const canActivate = usePermission(PERMISSIONS.THEME_ACTIVATE);

  // Ownership: solo el creador o un Owner (wildcard) pueden borrar la
  // plantilla. Esto evita que Admin/Designer borren plantillas de otros.
  // El backend tiene el mismo check; este es para no mostrar el botón
  // si el user no podrá usarlo de todos modos.
  const session = useSession((s) => s.user);
  const isOwnerWildcard = session?.permissions.includes(PERMISSION_ALL) ?? false;
  const isTemplateCreator =
    session != null && template.createdByUserId === session.id;
  const canDeleteThisTemplate = isOwnerWildcard || isTemplateCreator;

  const [mode, setMode] = useState<ThemeMode>("dark");
  const [name, setName] = useState(template.name);
  const [state, setState] = useState<ThemeStateLocal>(() => buildLocalStateFromTemplate(template));
  const initialState = useMemo(() => buildLocalStateFromTemplate(template), [template]);

  // Resync cuando cambia la plantilla seleccionada
  useEffect(() => {
    setName(template.name);
    setState(buildLocalStateFromTemplate(template));
  }, [template.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = !statesEqual(state, initialState) || name !== template.name;

  const invalidate = () => qc.invalidateQueries({ queryKey: ["theme", "templates"] });

  const save = useMutation({
    mutationFn: () =>
      themeApi.update(template.id, {
        name: name !== template.name ? name : undefined,
        tokens: serializeToTokens(state),
      }),
    onSuccess: () => {
      push({ kind: "success", message: "Cambios guardados." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error al guardar." }),
  });

  const duplicate = useMutation({
    mutationFn: async (newName: string) => themeApi.snapshot(newName, false),
    onSuccess: (tpl) => {
      push({ kind: "success", message: `Plantilla "${tpl.name}" duplicada como borrador.` });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error al duplicar." }),
  });

  const publishMut = useMutation({
    mutationFn: () => themeApi.publish(template.id),
    onSuccess: () => {
      push({ kind: "success", message: "Plantilla publicada al catálogo del usuario." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error al publicar." }),
  });

  const unpublishMut = useMutation({
    mutationFn: () => themeApi.unpublish(template.id),
    onSuccess: () => {
      push({ kind: "success", message: "Plantilla movida a borrador." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error al despublicar." }),
  });

  const activateMut = useMutation({
    mutationFn: () => themeApi.activate(template.id),
    onSuccess: () => {
      push({ kind: "success", message: "Plantilla aplicada a producción." });
      invalidate();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error al activar." }),
  });

  const deleteMut = useMutation({
    mutationFn: () => themeApi.delete(template.id),
    onSuccess: () => {
      push({ kind: "success", message: "Plantilla eliminada." });
      invalidate();
      onDeleted();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error al eliminar." }),
  });

  const openPreview = () => {
    const token = getToken();
    if (!token) {
      push({ kind: "error", message: "Sesión expirada. Hacé login de nuevo." });
      return;
    }
    const host = window.location.hostname;
    const portalOrigin =
      host === "localhost" || host === "127.0.0.1"
        ? window.location.origin
        : "https://mixcore.club";
    const url = `${portalOrigin}/?preview_theme=${template.id}#nexus_token=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener");
  };

  const handleDuplicate = async () => {
    const newName = await confirmAction({
      title: "Duplicar plantilla",
      message: "Crea una copia como borrador. Los tokens del borrador serán los del tema activo (no los locales sin guardar).",
      promptLabel: "Nombre de la copia",
      confirmLabel: "Duplicar",
    });
    if (newName !== null && (newName as string).trim()) duplicate.mutate((newName as string).trim());
  };

  const handleDelete = async () => {
    if (template.status === "active") {
      push({ kind: "error", message: "No se puede eliminar la plantilla activa." });
      return;
    }
    const ok = await confirmAction({
      title: `Eliminar "${template.name}"`,
      message:
        template.status === "published"
          ? "Está en el catálogo de usuarios. Los que la tengan elegida volverán al tema default."
          : "Es un borrador. Esta acción no es reversible.",
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (ok === true) deleteMut.mutate();
  };

  const busy =
    save.isPending ||
    publishMut.isPending ||
    unpublishMut.isPending ||
    activateMut.isPending ||
    deleteMut.isPending;

  return (
    <div className="flex flex-col gap-4">
      {/* Header de la plantilla en edición */}
      <header className="nx-card p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge status={template.status} />
          <input
            type="text"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nombre de la plantilla"
            disabled={!canEditDraft}
            className="nx-input flex-1 min-w-[200px] font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
          />
          {dirty && (
            <span className="text-2xs text-warning font-medium">● Sin guardar</span>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3 justify-end">
          <button onClick={openPreview} className="nx-btn-secondary text-2xs">
            <Eye size={12} /> Preview navegable
          </button>
          {canEditDraft && (
            <button onClick={handleDuplicate} disabled={busy} className="nx-btn-secondary text-2xs">
              <Copy size={12} /> Duplicar
            </button>
          )}
          {canEditDraft && (
            <button
              onClick={() => save.mutate()}
              disabled={busy || !dirty}
              className="nx-btn-secondary text-2xs"
            >
              {save.isPending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Guardar
            </button>
          )}
          {template.status === "draft" && canPublish && (
            <button
              onClick={async () => {
                if (dirty) {
                  push({ kind: "error", message: "Guardá los cambios antes de publicar." });
                  return;
                }
                publishMut.mutate();
              }}
              disabled={busy}
              className="nx-btn-secondary text-2xs"
            >
              <Upload size={12} /> Publicar
            </button>
          )}
          {template.status === "published" && canPublish && (
            <button
              onClick={async () => {
                const ok = await confirmAction({
                  title: "Despublicar",
                  message: "Vuelve a borrador. Usuarios que la tengan elegida verán el default.",
                  confirmLabel: "Despublicar",
                  tone: "danger",
                });
                if (ok === true) unpublishMut.mutate();
              }}
              disabled={busy}
              className="nx-btn-secondary text-2xs"
            >
              <Undo2 size={12} /> Despublicar
            </button>
          )}
          {template.status !== "active" && canActivate && (
            <button
              onClick={async () => {
                if (dirty) {
                  push({ kind: "error", message: "Guardá los cambios antes de activar." });
                  return;
                }
                const ok = await confirmAction({
                  title: `Aplicar "${template.name}" a producción`,
                  message:
                    "Se sirve a todos los usuarios sin preferencia propia. La plantilla activa anterior pasa a publicada. Inmediato.",
                  confirmLabel: "Aplicar",
                  tone: "success",
                });
                if (ok === true) activateMut.mutate();
              }}
              disabled={busy}
              className="nx-btn-primary text-2xs"
            >
              <CheckCircle2 size={12} /> Aplicar a producción
            </button>
          )}
          {template.status !== "active" && canEditDraft && canDeleteThisTemplate && (
            <button
              onClick={handleDelete}
              disabled={busy}
              className="nx-btn-ghost text-2xs text-danger hover:text-danger"
              title="Eliminar plantilla"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </header>

      {/* Selector de modo */}
      <div className="inline-flex bg-bg-subtle border border-border rounded-md p-0.5 self-start">
        {(["dark", "light"] as const).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={cn(
              "px-3 py-1.5 text-xs font-medium rounded-sm transition-colors flex items-center gap-1.5",
              mode === m ? "bg-bg-card text-fg shadow-nx-sm" : "text-fg-muted hover:text-fg",
            )}
          >
            <span>{m === "dark" ? "🌙" : "☀️"}</span>
            <span>{m === "dark" ? "Modo oscuro" : "Modo claro"}</span>
          </button>
        ))}
      </div>

      {/* Layout principal: editor (1fr) + preview (sticky) */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_minmax(320px,420px)] gap-4 items-start">
        <div className="nx-card p-4 flex flex-col gap-6">
          <ColorTokensEditor
            tokens={state[mode]}
            onChange={(key, value) =>
              setState((s) => ({ ...s, [mode]: { ...s[mode], [key]: value } }))
            }
            onError={(msg) => push({ kind: "error", message: msg })}
          />

          <hr className="border-border-subtle border-dashed" />

          <SliderEditor
            title="Efectos visuales"
            subtitle="(aplican a ambos modos)"
            metas={THEME_EFFECT_META}
            values={state.effects}
            onChange={(key, value) =>
              setState((s) => ({ ...s, effects: { ...s.effects, [key]: value } }))
            }
          />

          <hr className="border-border-subtle border-dashed" />

          <TypographyEditor
            typography={state.typography}
            onChange={(key, stack) =>
              setState((s) => ({ ...s, typography: { ...s.typography, [key]: stack } }))
            }
          />

          <hr className="border-border-subtle border-dashed" />

          <SliderEditor
            title="Forma"
            subtitle="(radio base, sharp/lg escalan auto)"
            metas={THEME_SHAPE_META}
            values={state.shape}
            onChange={(key, value) =>
              setState((s) => ({ ...s, shape: { ...s.shape, [key]: value } }))
            }
          />

          <hr className="border-border-subtle border-dashed" />

          <SliderEditor
            title="Densidad"
            subtitle="(padding y gap base de cards)"
            metas={THEME_DENSITY_META}
            values={state.density}
            onChange={(key, value) =>
              setState((s) => ({ ...s, density: { ...s.density, [key]: value } }))
            }
          />
        </div>

        <div className="xl:sticky xl:top-4">
          <h3 className="text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-2">
            Preview en vivo
          </h3>
          <LivePreview state={state} mode={mode} />
          <p className="text-2xs text-fg-subtle mt-2">
            Espejo del hero del portal user. Refleja los cambios sin necesidad de guardar.
          </p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: ThemeTemplate["status"] }) {
  const map = {
    draft: { cls: "nx-badge-neutral", label: "Borrador" },
    published: { cls: "nx-badge-info", label: "Publicado" },
    active: { cls: "nx-badge-success", label: "Activo" },
  } as const;
  const { cls, label } = map[status];
  return <span className={cls}>{label}</span>;
}
